
import React, { Suspense } from "react";
import { BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import { AppProviders } from "@/components/AppProviders";
import { QuickAddProvider } from "@/contexts/QuickAddContext";
import { PopupProvider } from "@/contexts/PopupContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { BusinessMetricsProvider, useBusinessMetricsDialog } from "@/contexts/BusinessMetricsContext";
import { TaskPopupProvider, useTaskPopup } from "@/contexts/TaskPopupContext";
import { NavigationPreferencesProvider } from "@/components/navigation/NavigationPreferencesProvider";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SubmenuSidebarProvider } from "@/contexts/SubmenuSidebarContext";
import { SidebarSearchProvider } from "@/contexts/SidebarSearchContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { AppRoutes } from "@/config/RouteConfig";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import QuickAddTask from "@/components/quick-add/QuickAddTask";
import { BusinessMetricsDialog } from "@/components/business-metrics/BusinessMetricsDialog";
import { TaskPopup } from "@/components/tasks/TaskPopup";
import { useQuickAdd } from "@/contexts/QuickAddContext";
import { useSprints } from "@/hooks/useSprints";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useHostnameTheme } from "@/hooks/useHostnameTheme";
import { useRouteMetadata } from "@/hooks/useRouteMetadata";
import { useClearHeavyPageCache } from "@/hooks/useClearHeavyPageCache";
import { ClientPortalProvider } from "@/contexts/ClientPortalContext";
import { ProposalPortalProvider } from "@/contexts/ProposalPortalContext";
import { SiteAuditPortalProvider } from "@/contexts/SiteAuditPortalContext";
import { LearnPortalProvider } from "@/contexts/LearnPortalContext";
import { SupportWidget } from "@/components/support/SupportWidget";
import { CookieConsentGlobal } from "@/components/cookie-consent/CookieConsentGlobal";
import { MemoryUsageBadge } from "@/components/MemoryUsageBadge";
import { NotificationStack } from "@/components/notifications/NotificationStack";
import { useAuth } from "@/hooks/useAuth";
import { BrandProvider } from "@/contexts/BrandProvider";
import { StickyStateProvider } from "@/contexts/StickyStateContext";
import { ProcessBulletinProvider } from "@/contexts/ProcessBulletinContext";
import { ProcessBulletinDialog } from "@/components/newsroom/ProcessBulletinDialog";
import { LinkBuildingPartnerProvider } from "@/contexts/LinkBuildingPartnerContext";
import { GlobalAddPartnerDialog } from "@/components/link-building/GlobalAddPartnerDialog";
import { ProductProvider } from "@/contexts/ProductContext";
import { GlobalProductDialog } from "@/components/brand-products/GlobalProductDialog";
import { BrandProductsFilterProvider } from "@/contexts/BrandProductsFilterContext";
import { CollaborationEmailProvider } from "@/contexts/CollaborationEmailContext";
import { GlobalEmailDialog } from "@/components/collaborations/GlobalEmailDialog";
import { CollaboratorDialogProvider } from "@/contexts/CollaboratorDialogContext";
import { GlobalCollaboratorDialog } from "@/components/collaborations/GlobalCollaboratorDialog";
import { NewsfeedSourceProvider } from "@/contexts/NewsfeedSourceContext";
import { GlobalNewsfeedSourceDialog } from "@/components/newsfeed/GlobalNewsfeedSourceDialog";
import { TestimonialDialogProvider } from "@/contexts/TestimonialDialogContext";
import { GlobalTestimonialDialog } from "@/components/testimonials/GlobalTestimonialDialog";
import { CopywritingInstructionsProvider } from "@/contexts/CopywritingInstructionsContext";
import { GlobalCopywritingInstructionsDialog } from "@/components/copywriting/GlobalCopywritingInstructionsDialog";
import { GrowthMetricDialogProvider } from "@/contexts/GrowthMetricDialogContext";
import { GlobalGrowthMetricDialog } from "@/components/growth-metrics/GlobalGrowthMetricDialog";
import { BrainstormingIdeaDialogProvider } from "@/contexts/BrainstormingIdeaDialogContext";
import GlobalBrainstormingIdeaDialog from "@/components/brainstorming/GlobalBrainstormingIdeaDialog";
import { IncomingTranscriptDialogProvider } from "@/contexts/IncomingTranscriptDialogContext";
import { GlobalIncomingTranscriptDialog } from "@/components/incoming-transcripts/GlobalIncomingTranscriptDialog";
import { ReportShareDialogProvider } from "@/contexts/ReportShareDialogContext";
import { GlobalReportShareDialog } from "@/components/report-shares/GlobalReportShareDialog";
import { InternalTrainingShareDialogProvider } from "@/contexts/InternalTrainingShareDialogContext";
import { GlobalInternalTrainingShareDialog } from "@/components/internal-training-shares/GlobalInternalTrainingShareDialog";
import { LearningCenterShareDialogProvider } from "@/contexts/LearningCenterShareDialogContext";
import { GlobalLearningCenterShareDialog } from "@/components/learning-center-shares/GlobalLearningCenterShareDialog";
import { CreateTaskDialogProvider } from "@/contexts/CreateTaskDialogContext";
import { GlobalCreateTaskDialog } from "@/components/growth-tasks/GlobalCreateTaskDialog";
import { EditIdeaDialogProvider } from "@/contexts/EditIdeaDialogContext";
import { GlobalEditIdeaDialog } from "@/components/scale-2y3x/GlobalEditIdeaDialog";
import { EditReferralEmailFlowDialogProvider } from "@/contexts/EditReferralEmailFlowDialogContext";
import { GlobalEditReferralEmailFlowDialog } from "@/components/referral/GlobalEditReferralEmailFlowDialog";
import { ReferralProgramEditProvider } from "@/contexts/ReferralProgramEditContext";
import { GlobalReferralProgramEditDialog } from "@/components/referral/GlobalReferralProgramEditDialog";
import { DocHubDialogProvider } from "@/contexts/DocHubDialogContext";
import { DocHubDialogs } from "@/components/documentation-hub/dialogs";
import { JournalistDialogProvider } from "@/contexts/JournalistDialogContext";
import { GlobalJournalistDialog } from "@/components/journalists/GlobalJournalistDialog";
import { ArticleEditorProvider } from "@/contexts/ArticleEditorContext";
import { GlobalArticleEditor } from "@/components/articles/GlobalArticleEditor";
import { ArticleAuthorProvider } from "@/contexts/ArticleAuthorContext";
import { GlobalArticleAuthorDialog } from "@/components/articles/GlobalArticleAuthorDialog";
import { ArticleCategoryProvider } from "@/contexts/ArticleCategoryContext";
import { GlobalArticleCategoryDialog } from "@/components/articles/GlobalArticleCategoryDialog";
import { ArticleTagProvider } from "@/contexts/ArticleTagContext";
import { GlobalArticleTagDialog } from "@/components/articles/GlobalArticleTagDialog";
import { QuizEditorProvider } from "@/contexts/QuizEditorContext";
import { GlobalQuizEditor } from "@/components/quiz/GlobalQuizEditor";
import { SalesPromptDialogProvider } from "@/contexts/SalesPromptDialogContext";
import { GlobalSalesPromptDialog } from "@/components/sales/GlobalSalesPromptDialog";
import { SuperAdminApiTokensProvider } from "@/contexts/SuperAdminApiTokensContext";
import { SuperAdminInviteProvider } from "@/contexts/SuperAdminInviteContext";
import { GlobalApiTokensDialog } from "@/components/super-admin/GlobalApiTokensDialog";
import { GlobalInviteTeamMemberDialog } from "@/components/super-admin/GlobalInviteTeamMemberDialog";
import { updateManifestIcons } from "@/utils/manifestIcon";
import { supabase } from "@/integrations/supabase/client";
import { useActivityTracking } from "@/hooks/useActivityTracking";
import { EvaluationCriteriaDialogProvider } from "@/contexts/EvaluationCriteriaDialogContext";
import { GlobalEvaluationCriteriaDialog } from "@/components/internal-training/GlobalEvaluationCriteriaDialog";
import { EngagementPointDialogProvider } from "@/contexts/EngagementPointDialogContext";
import { GlobalEngagementPointDialog } from "@/components/engagement-points/GlobalEngagementPointDialog";
import { PerformanceBriefPromptProvider } from "@/contexts/PerformanceBriefPromptContext";
import { GlobalPerformanceBriefPromptDialog } from "@/components/performance-brief/GlobalPerformanceBriefPromptDialog";
import { KeywordAssistantEditorProvider } from "@/contexts/KeywordAssistantEditorContext";
import { GlobalKeywordAssistantEditor } from "@/components/keyword-assistant/GlobalKeywordAssistantEditor";
import { SeoPagesEditorProvider } from "@/contexts/SeoPagesEditorContext";
import { GlobalSeoPagesEditor } from "@/components/seo/GlobalSeoPagesEditor";
import { SeoCannibalizationEditorProvider } from "@/contexts/SeoCannibalizationEditorContext";
import { GlobalSeoCannibalizationEditor } from "@/components/seo/GlobalSeoCannibalizationEditor";
import { SeoKeywordsEditorProvider } from "@/contexts/SeoKeywordsEditorContext";
import { GlobalSeoKeywordsEditor } from "@/components/seo/GlobalSeoKeywordsEditor";
import { SeoAiInsightsProvider } from "@/contexts/SeoAiInsightsContext";
import { InternalLinkingProvider } from "@/contexts/InternalLinkingContext";
import { GrowthOnePagerEditorProvider } from "@/contexts/GrowthOnePagerEditorContext";
import { GlobalGrowthOnePagerEditor } from "@/components/growth-onepager/GlobalGrowthOnePagerEditor";
import { VisualsCreationEditorProvider } from "@/contexts/VisualsCreationEditorContext";
import { GlobalVisualsCreationEditor } from "@/components/visuals-creation/GlobalVisualsCreationEditor";
import { AnnotationModeProvider } from "@/contexts/AnnotationModeContext";
import { CLAProgressProvider } from "@/contexts/CLAProgressContext";
import { GlobalCLAProgressDrawer } from "@/components/cla/GlobalCLAProgressDrawer";
import { GlobalAnnotationOverlay } from "@/components/annotations/GlobalAnnotationOverlay";
import { PodcastEpisodeDialogProvider } from "@/contexts/PodcastEpisodeDialogContext";
import { GlobalPodcastEpisodeDialog } from "@/components/podcast-host/GlobalPodcastEpisodeDialog";
import { PodcastHostDialogProvider } from "@/contexts/PodcastHostDialogContext";
import { GlobalPodcastHostDialog } from "@/components/podcast-host/GlobalPodcastHostDialog";
import { PodcastGuestDialogProvider } from "@/contexts/PodcastGuestDialogContext";
import { GlobalPodcastGuestDialog } from "@/components/podcast-host/GlobalPodcastGuestDialog";
import { SquadMemberDialogProvider } from "@/contexts/SquadMemberDialogContext";
import { GlobalSquadMemberDialog } from "@/components/growth-squad/GlobalSquadMemberDialog";
import { GrowthStandupDialogProvider } from "@/contexts/GrowthStandupDialogContext";
import { GlobalGrowthStandupDialogs } from "@/components/growth-standup/GlobalGrowthStandupDialogs";
import { LessonEditorProvider } from "@/contexts/LessonEditorContext";
import { GlobalLessonEditor } from "@/components/learn/admin/GlobalLessonEditor";
import { LMSLeadDialogProvider } from "@/contexts/LMSLeadDialogContext";
import { GlobalLMSLeadDialog } from "@/components/lms/GlobalLMSLeadDialog";
import { EventLeadDialogProvider } from "@/contexts/EventLeadDialogContext";
import { GlobalEventLeadDialog } from "@/components/events/GlobalEventLeadDialog";
import { EventDialogProvider } from "@/contexts/EventDialogContext";
import { GlobalEventDialog } from "@/components/events/GlobalEventDialog";
import { EventSpeakerDialogProvider } from "@/contexts/EventSpeakerDialogContext";
import { GlobalEventSpeakerDialog } from "@/components/events/GlobalEventSpeakerDialog";
import { PressReleaseCreateProvider } from "@/contexts/PressReleaseCreateContext";
import { GlobalPressReleaseCreateDialog } from "@/components/press-releases/GlobalPressReleaseCreateDialog";
import { BrandProductsDataProvider } from "@/contexts/BrandProductsDataContext";


function AppContent() {
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  
  // Public routes that should NEVER trigger authentication
  const isAuthPage = location.pathname === '/auth';
  const isWelcomePage = location.pathname === '/welcome';
  const isHomePage = location.pathname === '/home';
  const isConsultantApplicationPage = location.pathname === '/consultant-application';
  const isSeoDataCheckPage = location.pathname === '/Check-your-SEO-Data';
  const isTrialExpiredPage = location.pathname === '/trial-expired';
  const isClientPortal = location.pathname.startsWith('/client/');
  const isPromptStorePage = location.pathname === '/prompt-store';
  const isEmbedPage = location.pathname.startsWith('/embed/');
  const isPublicQuizPage = location.pathname.startsWith('/quiz/') && !location.pathname.startsWith('/quiz-') && !location.pathname.startsWith('/quizzes');
  const isPublicPollPage = location.pathname.startsWith('/poll/');
  const isPublicSurveyPage = location.pathname.startsWith('/survey/');
  const isPublicMiniGamePage = location.pathname.startsWith('/mini-game/') || location.pathname.includes('/preview');
  const isProposalPortal = location.pathname.startsWith('/proposal/');
  const isPublicChatWidgetPreview = location.pathname === '/public-chat-widget';
  const isPublicBadgeClaimPage = location.pathname.startsWith('/badges/claim/');
  const isPublicOfferPage = location.pathname.startsWith('/offer/');
  const isLearnPortal = location.pathname.startsWith('/learn/');
  const isPublicCoursePage = location.pathname.startsWith('/courses') && 
    !location.pathname.startsWith('/courses/embed-guide');
  const isSiteAuditClient = location.pathname.startsWith('/siteaudit/client/');
  const isPersonalCardPage = location.pathname.startsWith('/incoming/');
  const isViewPricesPage = location.pathname === '/view-prices';
  const isShortUrlRedirectPage = location.pathname.startsWith('/s/');
  const isSubmitTestimonialPage = location.pathname === '/submit-testimonial';
  const isPublicTestimonialsEmbed = location.pathname === '/testimonials/embed';
  const isSharedAnnotationReportPage = location.pathname.startsWith('/shared/annotation-report/');
  const isSharedCustomReportPage = location.pathname.startsWith('/shared/custom-report/');
  const isReferralJoinPage = location.pathname.startsWith('/join/') || location.pathname === '/referral/join';
  const isEventMaterialsPage = location.pathname.startsWith('/event-materials/');
  const isEventCheckInPage = location.pathname.startsWith('/event-check-in/');
  const isEventQuestionsPage = location.pathname.startsWith('/event-questions/');
  
  // Check if this is a 404 page for unauthenticated users (not a known public route)
  const isPublicAcademyPage = location.pathname.startsWith('/academies') && 
    !location.pathname.startsWith('/academies/embed-guide');
  const is2y3xAuthPage = location.pathname === '/2y3x/auth';
  const isKnownPublicRoute = isAuthPage || isWelcomePage || is2y3xAuthPage || isHomePage || isSeoDataCheckPage || isTrialExpiredPage || 
    isClientPortal || isPromptStorePage || isEmbedPage || isPublicQuizPage || isPublicPollPage || isPublicSurveyPage ||
    isPublicMiniGamePage || isProposalPortal || isPublicChatWidgetPreview || isPublicBadgeClaimPage || 
    isPublicOfferPage || isLearnPortal || isPublicCoursePage || isPublicAcademyPage || isSiteAuditClient || isPersonalCardPage || isViewPricesPage || isShortUrlRedirectPage || isSubmitTestimonialPage || isPublicTestimonialsEmbed || isSharedAnnotationReportPage || isSharedCustomReportPage || isReferralJoinPage || isEventMaterialsPage || isEventCheckInPage || isEventQuestionsPage;
  
  // If user is not authenticated and not on a known public route, treat as 404 (render without layout)
  const isUnauthenticated404 = !authLoading && !user && !isKnownPublicRoute;
  const { isQuickAddOpen, setIsQuickAddOpen } = useQuickAdd();
  const { isDialogOpen, setIsDialogOpen, editingMetric, selectedBrandId } = useBusinessMetricsDialog();
  const { isOpen: isTaskPopupOpen, selectedTask, taskType, closeTaskPopup, onStatusUpdate, onCommentsUpdate, onRefetch } = useTaskPopup();
  const { sprints } = useSprints();
  const { currentBrandId, currentBrand } = useBrandManager();
  
  // Apply hostname-based theming
  useHostnameTheme();
  
  // Apply route-based metadata automatically
  useRouteMetadata();

  // Clear React Query cache when leaving heavy pages to free memory
  useClearHeavyPageCache();
  
  // Track user activity time per route (lightweight session-based)
  useActivityTracking();

  const navigate = useNavigate();

  // Ensure brand_id is present on public course pages
  React.useEffect(() => {
    if (isPublicCoursePage) {
      const params = new URLSearchParams(location.search);
      const hasBrand = params.get('brand_id');
      if (!hasBrand && currentBrandId) {
        params.set('brand_id', currentBrandId);
        navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
      }
    }
  }, [isPublicCoursePage, location.pathname, location.search, currentBrandId, navigate]);

  // Update manifest to use brand's icon (affects left icon in push notifications)
  // Uses push notification icon if configured, otherwise falls back to favicon
  React.useEffect(() => {
    if (currentBrand?.id) {
      updateManifestIcons(currentBrand.id, supabase);
    }
  }, [currentBrand?.id]);

  // Find current sprint (open sprint within date range)
  const currentSprint = sprints.find(sprint => {
    const now = new Date();
    const startDate = new Date(sprint.start_date);
    const endDate = new Date(sprint.end_date);
    return now >= startDate && now <= endDate && sprint.status === 'open';
  }) || sprints.find(sprint => sprint.status === 'open');

  // Render public routes without any auth wrapper or layout
  // Include unauthenticated 404 pages (unknown routes for non-authenticated users)
  if (isAuthPage || isWelcomePage || is2y3xAuthPage || isHomePage || isConsultantApplicationPage || isSeoDataCheckPage || isTrialExpiredPage || isClientPortal || isPromptStorePage || isEmbedPage || isPublicQuizPage || isPublicPollPage || isPublicSurveyPage || isPublicMiniGamePage || isProposalPortal || isPublicChatWidgetPreview || isPublicBadgeClaimPage || isPublicOfferPage || isLearnPortal || isPublicCoursePage || isPublicAcademyPage || isSiteAuditClient || isPersonalCardPage || isViewPricesPage || isShortUrlRedirectPage || isSubmitTestimonialPage || isPublicTestimonialsEmbed || isSharedAnnotationReportPage || isSharedCustomReportPage || isReferralJoinPage || isEventMaterialsPage || isEventCheckInPage || isEventQuestionsPage || isUnauthenticated404) {
    return (
      <ClientPortalProvider>
        <ProposalPortalProvider>
          <SiteAuditPortalProvider>
            <LearnPortalProvider>
              <Suspense fallback={<div>Loading...</div>}>
                <AppRoutes />
              </Suspense>
              <CookieConsentGlobal />
            </LearnPortalProvider>
          </SiteAuditPortalProvider>
        </ProposalPortalProvider>
      </ClientPortalProvider>
    );
  }

  return (
    <SettingsProvider>
      <NavigationPreferencesProvider>
        <SidebarProvider>
          <SidebarSearchProvider>
          <SubmenuSidebarProvider>
          <div className="min-h-screen flex w-full">
             <AppLayout>
            <Suspense fallback={<div>Loading...</div>}>
              <AppRoutes />
            </Suspense>
        </AppLayout>
        
        {/* Global Quick Add Task Dialog */}
        <QuickAddTask
          open={isQuickAddOpen}
          onOpenChange={setIsQuickAddOpen}
          currentSprintId={currentSprint?.id || null}
        />
        
        {/* Global Business Metrics Dialog */}
        {selectedBrandId && (
          <BusinessMetricsDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            brandId={selectedBrandId}
            editingMetric={editingMetric}
          />
        )}

        {/* Global Task Popup */}
        <TaskPopup
          isOpen={isTaskPopupOpen}
          onClose={closeTaskPopup}
          task={selectedTask}
          taskType={taskType}
          onStatusUpdate={onStatusUpdate}
          onCommentsUpdate={onCommentsUpdate}
          onRefetch={onRefetch}
        />
        
        {/* Support Widget - Only show for authenticated users */}
        {user && <SupportWidget />}

        {/* Memory usage badge - super admins only (Chrome) */}
        <MemoryUsageBadge />
        
        {/* Push Notification Stack - Only show for authenticated users */}
        {user && <NotificationStack />}

        {/* Cookie consent banner - when cookie-consent plugin is active for current brand */}
        <CookieConsentGlobal />
        
        {/* Global Process Bulletin Dialog */}
        <ProcessBulletinDialog />
        
        {/* Global Add Partner Dialog */}
        <GlobalAddPartnerDialog />
        
        {/* Global Product Dialog */}
        <GlobalProductDialog />
        
        {/* Global Collaboration Email Dialog */}
        <GlobalEmailDialog />
        
        {/* Global Collaborator Dialog */}
        <GlobalCollaboratorDialog />
        
        {/* Global Newsfeed Source Dialog */}
        <GlobalNewsfeedSourceDialog />
        
        {/* Global Testimonial Dialog */}
        <GlobalTestimonialDialog />
        
        {/* Global Copywriting Instructions Dialog */}
        <GlobalCopywritingInstructionsDialog />
        
        {/* Global Growth Metric Dialog */}
        <GlobalGrowthMetricDialog />
        
        {/* Global Brainstorming Idea Dialog */}
        <GlobalBrainstormingIdeaDialog />
        
        {/* Global Incoming Transcript Dialog */}
        <GlobalIncomingTranscriptDialog />
        
        {/* Global Report Share Dialog */}
        <GlobalReportShareDialog />
        
        {/* Global Internal Training Share Dialog */}
        <GlobalInternalTrainingShareDialog />
        
        {/* Global Learning Center Share Dialog */}
        <GlobalLearningCenterShareDialog />
        {/* Global Create Task Dialog */}
        <GlobalCreateTaskDialog />
        
        {/* Global Doc Hub Dialogs (Snippets, Tips, Documents, Events) */}
        <DocHubDialogs />
        
        
        {/* Global Journalist Dialog */}
        <GlobalJournalistDialog />
        
        {/* Global Article Editor */}
        <GlobalArticleEditor />
        
        {/* Global Article Author Dialog */}
        <GlobalArticleAuthorDialog />
        
        {/* Global Article Category Dialog */}
        <GlobalArticleCategoryDialog />
        
        {/* Global Article Tag Dialog */}
        <GlobalArticleTagDialog />
        
        {/* Global Quiz Editor */}
        <GlobalQuizEditor />
        
        {/* Global Lesson Editor */}
        <GlobalLessonEditor />
        
        {/* Global Sales Prompt Dialog */}
        <GlobalSalesPromptDialog />
        
        {/* Global Super Admin Dialogs */}
        <GlobalApiTokensDialog />
        <GlobalInviteTeamMemberDialog />
        
        {/* Global Evaluation Criteria Dialog */}
        <GlobalEvaluationCriteriaDialog />
        
        {/* Global Engagement Point Dialog */}
        <GlobalEngagementPointDialog />
        
        {/* Global Performance Brief Prompt Dialog */}
        <GlobalPerformanceBriefPromptDialog />
        
        {/* Global Keyword Assistant Editor */}
        <GlobalKeywordAssistantEditor />
        
        {/* Global SEO Pages Editor */}
        <GlobalSeoPagesEditor />
        
        {/* Global SEO Cannibalization Editor */}
        <GlobalSeoCannibalizationEditor />
        
        {/* Global SEO Keywords Editor */}
        <GlobalSeoKeywordsEditor />
        
        {/* Global Growth One-Pager Editor */}
        <GlobalGrowthOnePagerEditor />
        
        {/* Global Visuals Creation Editor */}
        <GlobalVisualsCreationEditor />
        
        {/* Global CLA Progress Drawer */}
        <GlobalCLAProgressDrawer />
        
        {/* Global Podcast Episode Dialog */}
        <GlobalPodcastEpisodeDialog />
        
        {/* Global Podcast Host Dialog */}
        <GlobalPodcastHostDialog />
        
        {/* Global Event Speaker Dialog */}
        <GlobalEventDialog />
        <GlobalEventSpeakerDialog />
        
        {/* Global Podcast Guest Dialog */}
        <GlobalPodcastGuestDialog />
        
        {/* Global Squad Member Dialog */}
        <GlobalSquadMemberDialog />
        
        {/* Global Growth Standup Dialogs (Day Start / Day End) */}
        <GlobalGrowthStandupDialogs />
        
        {/* Global LMS Lead Dialog */}
        <GlobalLMSLeadDialog />
        {/* Global Event Lead Dialog */}
        <GlobalEventLeadDialog />
        
        {/* Global Press Release Create Dialog */}
        <GlobalPressReleaseCreateDialog />
        </div>
          </SubmenuSidebarProvider>
          </SidebarSearchProvider>
      </SidebarProvider>
      </NavigationPreferencesProvider>
    </SettingsProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppProviders>
          <BrandProvider>
            <PopupProvider>
              <BusinessMetricsProvider>
                 <TaskPopupProvider>
                     <QuickAddProvider>
                      <LinkBuildingPartnerProvider>
                        <ProductProvider>
                          <BrandProductsFilterProvider>
                          <CollaborationEmailProvider>
                            <CollaboratorDialogProvider>
                            <NewsfeedSourceProvider>
                              <TestimonialDialogProvider>
                                <CopywritingInstructionsProvider>
                                  <GrowthMetricDialogProvider>
                                    <BrainstormingIdeaDialogProvider>
                                      <IncomingTranscriptDialogProvider>
                                        <ReportShareDialogProvider>
                                          <InternalTrainingShareDialogProvider>
                                            <LearningCenterShareDialogProvider>
                                              <CreateTaskDialogProvider>
                              <EditIdeaDialogProvider>
                                <GlobalEditIdeaDialog />
                                <EditReferralEmailFlowDialogProvider>
                                  <GlobalEditReferralEmailFlowDialog />
                                  <ReferralProgramEditProvider>
                                    <GlobalReferralProgramEditDialog />
                                <DocHubDialogProvider>
                                  <JournalistDialogProvider>
                                    <ArticleEditorProvider>
                                    <ArticleAuthorProvider>
                                    <ArticleCategoryProvider>
                                    <ArticleTagProvider>
                                    <QuizEditorProvider>
                                    <LessonEditorProvider>
                                    <SalesPromptDialogProvider>
                                    <SuperAdminApiTokensProvider>
                                    <SuperAdminInviteProvider>
                                    <EvaluationCriteriaDialogProvider>
                                    <EngagementPointDialogProvider>
                                    <PerformanceBriefPromptProvider>
                                    <KeywordAssistantEditorProvider>
                                    <SeoPagesEditorProvider>
                                    <SeoCannibalizationEditorProvider>
                                    <SeoKeywordsEditorProvider>
                                    <SeoAiInsightsProvider>
                                    <InternalLinkingProvider>
                                    <GrowthOnePagerEditorProvider>
                                    <VisualsCreationEditorProvider>
                                    <ProcessBulletinProvider>
                                    <AnnotationModeProvider>
                                    <CLAProgressProvider>
                                    <PodcastEpisodeDialogProvider>
                                    <PodcastHostDialogProvider>
                                    <EventDialogProvider>
                                    <EventSpeakerDialogProvider>
                                    <PodcastGuestDialogProvider>
                                    <SquadMemberDialogProvider>
                                    <GrowthStandupDialogProvider>
                                    <LMSLeadDialogProvider>
                                    <EventLeadDialogProvider>
                                    <PressReleaseCreateProvider>
                                    <BrandProductsDataProvider>
                                                    <ProposalPortalProvider>
                                                       <StickyStateProvider>
                                                          <AppContent />
                                                        </StickyStateProvider>
                                                      </ProposalPortalProvider>
                                    </BrandProductsDataProvider>
                                    </PressReleaseCreateProvider>
                                    </EventLeadDialogProvider>
                                    </LMSLeadDialogProvider>
                                    </GrowthStandupDialogProvider>
                                    </SquadMemberDialogProvider>
                                    </PodcastGuestDialogProvider>
                                    </EventSpeakerDialogProvider>
                                    </EventDialogProvider>
                                    </PodcastHostDialogProvider>
                                    </PodcastEpisodeDialogProvider>
                                    </CLAProgressProvider>
                                    </AnnotationModeProvider>
                                    </ProcessBulletinProvider>
                                    </VisualsCreationEditorProvider>
                                    </GrowthOnePagerEditorProvider>
                                    </InternalLinkingProvider>
                                    </SeoAiInsightsProvider>
                                    </SeoKeywordsEditorProvider>
                                    </SeoCannibalizationEditorProvider>
                                    </SeoPagesEditorProvider>
                                    </KeywordAssistantEditorProvider>
                                    </PerformanceBriefPromptProvider>
                                    </EngagementPointDialogProvider>
                                    </EvaluationCriteriaDialogProvider>
                                    </SuperAdminInviteProvider>
                                    </SuperAdminApiTokensProvider>
                                    </SalesPromptDialogProvider>
                                    </LessonEditorProvider>
                                    </QuizEditorProvider>
                                    </ArticleTagProvider>
                                    </ArticleCategoryProvider>
                                    </ArticleAuthorProvider>
                                    </ArticleEditorProvider>
                                  </JournalistDialogProvider>
                                </DocHubDialogProvider>
                                  </ReferralProgramEditProvider>
                                </EditReferralEmailFlowDialogProvider>
                              </EditIdeaDialogProvider>
                                              </CreateTaskDialogProvider>
                                            </LearningCenterShareDialogProvider>
                                          </InternalTrainingShareDialogProvider>
                                        </ReportShareDialogProvider>
                                      </IncomingTranscriptDialogProvider>
                                    </BrainstormingIdeaDialogProvider>
                                  </GrowthMetricDialogProvider>
                                </CopywritingInstructionsProvider>
                              </TestimonialDialogProvider>
                            </NewsfeedSourceProvider>
                            </CollaboratorDialogProvider>
                          </CollaborationEmailProvider>
                          </BrandProductsFilterProvider>
                        </ProductProvider>
                      </LinkBuildingPartnerProvider>
                    </QuickAddProvider>
                </TaskPopupProvider>
              </BusinessMetricsProvider>
            </PopupProvider>
          </BrandProvider>
        </AppProviders>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
